import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators
} from '@angular/forms';
import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';
import { map } from 'rxjs/operators';

import { IFBConsentForm, IFBContentFormFieldResponse } from '@app/models/consent-form';
import { formGroupValidator } from '@app/modules/ui/validators/form-group-validator';
import { TranslateService } from '@app/shared/modules/translate/services/translate.service';
import { CompanyService } from '@app/shared/services/company.service';

@Component({
  selector: 'app-consent-form',
  templateUrl: './consent-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConsentFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ConsentFormComponent),
      multi: true
    }
  ],
  standalone: false
})
export class ConsentFormComponent implements OnChanges, ControlValueAccessor, Validator {
  @Input() fields: IFBConsentForm[];
  @Input() showErrors: boolean;
  @Output() updatedValidity = new EventEmitter<boolean>();

  isLoaded = false;
  locale: string;
  response: IFBContentFormFieldResponse[] = [];

  onTouched: () => void;

  responseForm = new UntypedFormGroup(
    {},
    {
      // Group level validator to announce custom form validation status to parent
      // (required when run by angularjs in hybrid mode)
      validators: formGroupValidator(this.updatedValidity)
    }
  );

  constructor(
    private company: CompanyService,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fields && changes.fields.currentValue) {
      const fields = changes.fields.currentValue;

      this.locale = this.defineLocale(fields);

      this.fields = fields;

      // init checkboxes unchecked
      this.response = fields.map(field => ({
        name: field.name,
        value: false
      }));

      this.fields.forEach(field => {
        this.responseForm.addControl(field.name, new UntypedFormControl(false, Validators.requiredTrue));
      });

      // Trigger initial form change to propagate response value to parent
      this.responseForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });

      this.isLoaded = true;
    }

    if (changes.showErrors && changes.showErrors.currentValue) {
      for (const key in this.responseForm.controls) {
        if (this.responseForm.controls.hasOwnProperty(key)) {
          this.responseForm.controls[key].markAsDirty();
        }
      }
    }
  }

  writeValue(value: string): void {
    // Don't expect value from the parent component
  }

  registerOnChange(fn: any): void {
    this.responseForm.valueChanges
      .pipe(
        // Watch form changes, but return formatted response data
        map(() => this.response)
      )
      .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl) {
    return this.responseForm.errors;
  }

  defineLocale(fields: IFBConsentForm[]): string {
    const companyLocale = this.company.data.languages.defaultLocale;
    const userLocale = this.translate.getLocale();

    const field = fields[0];

    if (!field || !field.translations || isEmpty(field.translations)) {
      // No translations available
      return '';
    }

    const translations = field.translations;

    // Try to use language which user currently using
    const inUserLocale = translations.hasOwnProperty(userLocale);

    if (inUserLocale) {
      return userLocale;
    }

    // Try to fall back to browser language
    const inCompanyLocale = translations.hasOwnProperty(companyLocale);

    if (inCompanyLocale) {
      return companyLocale;
    }

    // Fallback to first localization available
    return Object.keys(translations)[0];
  }

  propagateFieldUpdate($event: IFBContentFormFieldResponse): void {
    const fieldValue = find(this.response, { name: $event.name });
    fieldValue.value = $event.value;

    this.onTouched();
  }
}
